import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/Header'
import { useColors, formatCurrency } from '@/lib/utils'
import { useThemeStore } from '@/stores/theme'
import { FileText, Download, Table, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { format } from 'date-fns'
import { useState } from 'react'

export const Route = createFileRoute('/_authenticated/weekly')({
  component: WeeklyPage,
})

// Mock data matching original
const weeklyStoreRankings = [
  {
    id: '1',
    name: 'Downtown',
    sales: 245789.45,
    previousSales: 228950.22,
    percentChange: 7.35,
    rank: 1,
    previousRank: 1,
  },
  {
    id: '2',
    name: 'Westside Mall',
    sales: 198562.30,
    previousSales: 175623.89,
    percentChange: 13.06,
    rank: 2,
    previousRank: 3,
  },
  {
    id: '3',
    name: 'Northgate',
    sales: 187456.78,
    previousSales: 195230.45,
    percentChange: -3.98,
    rank: 3,
    previousRank: 2,
  },
  {
    id: '4',
    name: 'Eastport',
    sales: 165897.21,
    previousSales: 155678.34,
    percentChange: 6.56,
    rank: 4,
    previousRank: 4,
  },
  {
    id: '5',
    name: 'Southside',
    sales: 142365.89,
    previousSales: 132456.78,
    percentChange: 7.48,
    rank: 5,
    previousRank: 5,
  },
]

const weeklyProfitLossData = [
  {
    id: '1',
    store: 'Downtown',
    revenue: 245789.45,
    cogs: 98315.78,
    grossProfit: 147473.67,
    laborCost: 53589.23,
    otherExpenses: 32546.89,
    netProfit: 61337.55,
    margin: 24.96,
  },
  {
    id: '2',
    store: 'Westside Mall',
    revenue: 198562.30,
    cogs: 79424.92,
    grossProfit: 119137.38,
    laborCost: 45668.33,
    otherExpenses: 27798.72,
    netProfit: 45670.33,
    margin: 23.00,
  },
  {
    id: '3',
    store: 'Northgate',
    revenue: 187456.78,
    cogs: 74982.71,
    grossProfit: 112474.07,
    laborCost: 43116.06,
    otherExpenses: 26243.95,
    netProfit: 43114.06,
    margin: 23.00,
  },
  {
    id: '4',
    store: 'Eastport',
    revenue: 165897.21,
    cogs: 66358.88,
    grossProfit: 99538.33,
    laborCost: 39815.33,
    otherExpenses: 21566.64,
    netProfit: 38156.36,
    margin: 23.00,
  },
  {
    id: '5',
    store: 'Southside',
    revenue: 142365.89,
    cogs: 56946.36,
    grossProfit: 85419.53,
    laborCost: 31320.50,
    otherExpenses: 19931.22,
    netProfit: 34167.81,
    margin: 24.00,
  },
]

const weeklyRegisterSummary = {
  totalDeletions: 36,
  totalCancellations: 28,
  totalOverrings: 42,
  totalPaidOuts: 15,
  amountDeletions: 1245.67,
  amountCancellations: 2356.89,
  amountOverrings: 865.23,
  amountPaidOuts: 3256.78,
}

function WeeklyPage() {
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  const [dateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 7)),
    end: new Date()
  })

  const handleDownload = (reportType: string, format: string) => {
    console.log(`Downloading ${reportType} in ${format} format`)
  }

  const getRankChangeIcon = (current: number, previous: number) => {
    if (current < previous) {
      return <ArrowUpRight size={16} color={Colors.success.main} />
    } else if (current > previous) {
      return <ArrowDownRight size={16} color={Colors.error.main} />
    }
    return <Minus size={16} color={Colors.neutral[700]} />
  }

  return (
    <div className="flex-1">
      <Header 
        title="Weekly Reports"
        subtitle={`${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`}
        onOpenFilters={() => {}}
      />

      <div className="p-6 space-y-6">
        {/* Register Activity Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Deletions
            </p>
            <p className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              {weeklyRegisterSummary.totalDeletions} (${weeklyRegisterSummary.amountDeletions.toLocaleString()})
            </p>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Cancellations
            </p>
            <p className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              {weeklyRegisterSummary.totalCancellations} (${weeklyRegisterSummary.amountCancellations.toLocaleString()})
            </p>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Overrings
            </p>
            <p className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              {weeklyRegisterSummary.totalOverrings} (${weeklyRegisterSummary.amountOverrings.toLocaleString()})
            </p>
          </div>

          <div 
            className="p-6 rounded-xl shadow-sm"
            style={{ 
              backgroundColor: Colors.background.paper,
              boxShadow: isDarkMode 
                ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
                : '0 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <p className="text-sm font-inter-medium mb-1" style={{ color: Colors.text.secondary }}>
              Paid Outs
            </p>
            <p className="text-xl font-inter-bold" style={{ color: Colors.text.primary }}>
              {weeklyRegisterSummary.totalPaidOuts} (${weeklyRegisterSummary.amountPaidOuts.toLocaleString()})
            </p>
          </div>
        </div>

        {/* Store Rankings */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Weekly Store Rankings
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderBottomColor: Colors.border.main }}>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Rank</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Store</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Sales</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Previous</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Change</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Prev Rank</th>
                </tr>
              </thead>
              <tbody>
                {weeklyStoreRankings.map((store) => (
                  <tr key={store.id} className="border-b" style={{ borderBottomColor: Colors.border.main }}>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        <span className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                          {store.rank}
                        </span>
                        {getRankChangeIcon(store.rank, store.previousRank)}
                      </div>
                    </td>
                    <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                      {store.name}
                    </td>
                    <td className="p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.sales)}
                    </td>
                    <td className="p-3 font-inter-regular" style={{ color: Colors.text.secondary }}>
                      {formatCurrency(store.previousSales)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-1">
                        {store.percentChange > 0 ? (
                          <ArrowUpRight size={16} color={Colors.success.main} />
                        ) : store.percentChange < 0 ? (
                          <ArrowDownRight size={16} color={Colors.error.main} />
                        ) : (
                          <Minus size={16} color={Colors.neutral[700]} />
                        )}
                        <span 
                          className="font-inter-medium text-sm"
                          style={{ 
                            color: store.percentChange > 0 
                              ? Colors.success.main 
                              : store.percentChange < 0 
                                ? Colors.error.main 
                                : Colors.neutral[700] 
                          }}
                        >
                          {Math.abs(store.percentChange).toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 font-inter-regular" style={{ color: Colors.text.secondary }}>
                      {store.previousRank}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* P&L Table */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-4" style={{ color: Colors.text.primary }}>
            Weekly Field P&L
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderBottomColor: Colors.border.main }}>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Store</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Revenue</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>COGS</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Gross Profit</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Labor</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Other Exp.</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Net Profit</th>
                  <th className="text-left p-3 font-inter-semibold text-sm" style={{ color: Colors.text.primary }}>Margin %</th>
                </tr>
              </thead>
              <tbody>
                {weeklyProfitLossData.map((store) => (
                  <tr key={store.id} className="border-b" style={{ borderBottomColor: Colors.border.main }}>
                    <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                      {store.store}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.revenue)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.cogs)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.grossProfit)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.laborCost)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.otherExpenses)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.text.primary }}>
                      {formatCurrency(store.netProfit)}
                    </td>
                    <td className="p-3 font-inter-medium" style={{ color: Colors.primary.main }}>
                      {store.margin.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reports Download Section */}
        <div 
          className="p-6 rounded-xl shadow-sm"
          style={{ 
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode 
              ? '0 2px 4px rgba(0, 0, 0, 0.2)' 
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <h3 className="text-lg font-inter-bold mb-6" style={{ color: Colors.text.primary }}>
            Download Reports
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Weekly Sales Ranking Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Weekly Sales Ranking
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Store performance rankings and sales comparisons
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('sales-ranking', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('sales-ranking', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>

            {/* Weekly Field P&L Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Weekly Field P&L
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Detailed profit and loss statements by location
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('field-pl', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('field-pl', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>

            {/* Weekly Register Activities Report */}
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: Colors.background.default }}
            >
              <div className="flex items-center space-x-2 mb-2">
                <FileText size={24} color={Colors.primary.main} />
                <h4 className="font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Register Activities
                </h4>
              </div>
              <p className="text-sm font-inter-regular mb-4" style={{ color: Colors.text.secondary }}>
                Detailed register activity logs and summaries
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDownload('register-activities', 'excel')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Table size={16} />
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload('register-activities', 'pdf')}
                  className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-inter-medium text-white"
                  style={{ backgroundColor: Colors.primary.main }}
                >
                  <Download size={16} />
                  <span>PDF</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}